import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft, AlertTriangle, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import DatePicker from '../components/time-off/DatePicker';
import BalanceDisplay from '../components/time-off/BalanceDisplay';
import TeamContext from '../components/time-off/TeamContext';
import { useToast } from '../components/common/Toast';
import './TimeOffSubmit.css';

function TimeOffSubmit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [category, setCategory] = useState('pto');
  const [selectedDays, setSelectedDays] = useState([]);
  const [notes, setNotes] = useState('');
  const [insufficientBalanceNote, setInsufficientBalanceNote] = useState('');
  const [balance, setBalance] = useState(null);
  const [teamContext, setTeamContext] = useState({});
  const [teamContextLoading, setTeamContextLoading] = useState(false);
  const [overlappingDates, setOverlappingDates] = useState([]);
  const [holidayDates, setHolidayDates] = useState([]);
  const [isShortNotice, setIsShortNotice] = useState(false);
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // W2 staff use externally-tracked PTO -- no balance, no short-notice, auto-approve.
  const isW2 = user?.employment_type === 'staff_w2';

  // Admin on-behalf
  const isAdmin = user?.role === 'admin';
  const [onBehalfUserId, setOnBehalfUserId] = useState('');
  const [userList, setUserList] = useState([]);

  // Load balance
  useEffect(() => {
    const targetUserId = onBehalfUserId || user.id;
    api.get(`/users/${targetUserId}/balance`)
      .then(data => setBalance(data.not_configured ? null : data))
      .catch(() => setBalance(null));
  }, [user.id, onBehalfUserId]);

  // Load user list for admin on-behalf
  useEffect(() => {
    if (isAdmin) {
      api.get('/users?status=active')
        .then(data => setUserList(data.users || []))
        .catch(() => {});
    }
  }, [isAdmin]);

  // Check team context, overlaps, and holidays when dates change
  const checkDates = useCallback(async () => {
    if (selectedDays.length === 0) {
      setTeamContext({});
      setOverlappingDates([]);
      setHolidayDates([]);
      setIsShortNotice(false);
      setInsufficientBalance(false);
      return;
    }

    const dateStrings = selectedDays.map(d => d.date);
    const datesParam = dateStrings.join(',');
    const targetUserId = onBehalfUserId || user.id;

    try {
      setTeamContextLoading(true);

      const [teamRes, overlapRes] = await Promise.all([
        api.get(`/requests/team-context?dates=${datesParam}&user_id=${targetUserId}`),
        api.get(`/requests/overlap-check?dates=${datesParam}&user_id=${targetUserId}`),
      ]);

      setTeamContext(teamRes.dates || {});
      setOverlappingDates(overlapRes.overlapping_dates || []);

      // Check holiday dates
      try {
        const holidayRes = await api.get(`/holidays/check?dates=${datesParam}&user_id=${targetUserId}`);
        setHolidayDates((holidayRes.holidays || []).map(h => h.date));
      } catch {
        setHolidayDates([]);
      }
    } catch {
      // Silently handle -- these are informational
    } finally {
      setTeamContextLoading(false);
    }
  }, [selectedDays, onBehalfUserId, user.id]);

  useEffect(() => {
    const timeout = setTimeout(checkDates, 300);
    return () => clearTimeout(timeout);
  }, [checkDates]);

  // Calculate total days and check balance
  useEffect(() => {
    const totalDays = selectedDays.reduce((sum, d) => {
      return sum + (d.day_type === 'half_am' || d.day_type === 'half_pm' ? 0.5 : 1.0);
    }, 0);

    if (balance && category === 'pto' && balance.available) {
      setInsufficientBalance(totalDays > balance.available.total);
    } else {
      setInsufficientBalance(false);
    }

    // Short notice check (simple client-side approximation)
    if (selectedDays.length > 0) {
      const sortedDates = [...selectedDays].sort((a, b) => a.date.localeCompare(b.date));
      const earliest = new Date(sortedDates[0].date + 'T00:00:00');
      const now = new Date();
      const diffDays = Math.ceil((earliest - now) / (1000 * 60 * 60 * 24));
      setIsShortNotice(diffDays <= 3);
    } else {
      setIsShortNotice(false);
    }
  }, [selectedDays, balance, category]);

  const totalDays = selectedDays.reduce((sum, d) => {
    return sum + (d.day_type === 'half_am' || d.day_type === 'half_pm' ? 0.5 : 1.0);
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedDays.length === 0) {
      toast.error('Please select at least one date');
      return;
    }

    if (overlappingDates.length > 0) {
      toast.error('Some selected dates overlap with existing requests');
      return;
    }

    if (insufficientBalance && !insufficientBalanceNote.trim()) {
      toast.error('Please provide a note explaining the insufficient balance request');
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        category,
        days: selectedDays,
        notes: notes.trim() || undefined,
        insufficient_balance_note: insufficientBalance ? insufficientBalanceNote.trim() : undefined,
      };

      if (onBehalfUserId) {
        body.user_id = onBehalfUserId;
      }

      await api.post('/requests', body);
      toast.success(
        onBehalfUserId
          ? 'Request submitted and auto-approved'
          : isW2
            ? 'Added to calendar -- auto-approved'
            : 'Time off request submitted'
      );
      navigate('/time-off');
    } catch (err) {
      toast.error(err.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="time-off-submit">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/time-off')}>
          <ArrowLeft size={18} />
        </button>
        <Calendar size={24} strokeWidth={2} />
        <h1>Request Time Off</h1>
      </div>

      <form onSubmit={handleSubmit} className="submit-form">
        <div className="submit-layout">
          {/* Left: Form */}
          <div className="submit-main">
            {/* Admin on-behalf */}
            {isAdmin && (
              <div className="form-group">
                <label className="form-label">Submit for</label>
                <select
                  className="form-select"
                  value={onBehalfUserId}
                  onChange={(e) => setOnBehalfUserId(e.target.value)}
                >
                  <option value="">Myself</option>
                  {userList
                    .filter(u => u.id !== user.id)
                    .map(u => (
                      <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                    ))}
                </select>
                {onBehalfUserId && (
                  <p className="form-hint">This request will be auto-approved.</p>
                )}
              </div>
            )}

            {/* Category */}
            <div className="form-group">
              <label className="form-label">Category</label>
              <div className="category-toggle">
                <button
                  type="button"
                  className={`category-btn ${category === 'pto' ? 'category-btn-active' : ''}`}
                  onClick={() => setCategory('pto')}
                >
                  PTO
                </button>
                <button
                  type="button"
                  className={`category-btn ${category === 'birthday' ? 'category-btn-active' : ''}`}
                  onClick={() => setCategory('birthday')}
                >
                  Birthday
                </button>
              </div>
              {category === 'birthday' && (
                <p className="form-hint">
                  Birthday day off must be taken during your birth month. Does not count against PTO balance.
                </p>
              )}
            </div>

            {/* Date Picker */}
            <div className="form-group">
              <label className="form-label">Select Dates</label>
              <DatePicker
                selectedDays={selectedDays}
                onChange={setSelectedDays}
                holidays={holidayDates}
                disabledDates={overlappingDates}
              />
            </div>

            {/* Warnings */}
            {holidayDates.length > 0 && (
              <div className="submit-warning">
                <AlertTriangle size={14} />
                <span>Some selected dates are holidays for your country calendar</span>
              </div>
            )}

            {isShortNotice && !isW2 && (
              <div className="submit-warning">
                <AlertTriangle size={14} />
                <span>This is a short notice request (within 3 business days)</span>
              </div>
            )}

            {insufficientBalance && !isW2 && (
              <div className="submit-warning submit-warning-error">
                <AlertTriangle size={14} />
                <span>Insufficient balance for this request ({totalDays} days requested, {balance?.available?.total || 0} available)</span>
              </div>
            )}

            {/* Insufficient balance note */}
            {insufficientBalance && !isW2 && (
              <div className="form-group">
                <label className="form-label form-label-required">Insufficient Balance Note</label>
                <textarea
                  className="form-textarea"
                  value={insufficientBalanceNote}
                  onChange={(e) => setInsufficientBalanceNote(e.target.value)}
                  placeholder="Please explain why you are requesting more time off than your available balance..."
                  rows={3}
                  required
                />
              </div>
            )}

            {/* Notes */}
            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <textarea
                className="form-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this request..."
                rows={3}
              />
            </div>

            {/* Team context */}
            <TeamContext teamData={teamContext} loading={teamContextLoading} />

            {/* Submit */}
            <div className="submit-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/time-off')}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting || selectedDays.length === 0}
              >
                <Send size={14} />
                {submitting ? 'Submitting...' : isW2 ? 'Add to Calendar' : 'Submit Request'}
              </button>
            </div>
          </div>

          {/* Right: Balance sidebar -- hidden for W2 (external tracking) */}
          {!isW2 && (
          <div className="submit-sidebar">
            <div className="submit-balance-card">
              <h4>Current Balance</h4>
              {balance ? (
                <BalanceDisplay balance={balance} />
              ) : (
                <p className="submit-balance-empty">No allocation set</p>
              )}
            </div>

            {selectedDays.length > 0 && (
              <div className="submit-summary-card">
                <h4>Request Summary</h4>
                <div className="submit-summary-row">
                  <span>Days requested</span>
                  <span className="submit-summary-value">{totalDays}</span>
                </div>
                {balance && category === 'pto' && (
                  <div className="submit-summary-row">
                    <span>Balance after</span>
                    <span className={`submit-summary-value ${
                      balance.available.total - totalDays < 0 ? 'submit-summary-negative' : ''
                    }`}>
                      {Math.max(0, balance.available.total - totalDays)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          )}
        </div>
      </form>
    </div>
  );
}

export default TimeOffSubmit;
